import React, { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Download, Info, Activity, Zap, Building2, ChevronDown, ChevronUp, Printer, TrendingDown, TrendingUp, BarChart3, LineChart as LineChartIcon, Settings2, ClipboardList, ShieldAlert, Lock, Unlock, Trash2, ArrowLeft, Share2, FileText, MessageSquare, AlertTriangle } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine, BarChart, Bar, Area, ComposedChart } from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import ShareModal from '../components/ShareModal';
import { MessageFournisseurButton } from '../components/turpe/MessageFournisseur';
import ValideClientBadge from '../components/ValideClientBadge';
import ShareAccessBadge from '../components/ShareAccessBadge';
import RapportAuditHistory from '../components/RapportAuditHistory';
import PdfPreviewModal from '../components/PdfPreviewModal';
import { downloadPdf } from '../utils/downloadPdf';
import SegmentChangeAlert, { tensionFromFormule } from '../components/turpe/SegmentChangeAlert';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RapportTURPEMulti = ({ readOnly = false, shareData = null, shareToken = null }) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth?.() || {};
  const userRole = (auth?.user?.role || '').toLowerCase();
  const isAdmin = userRole === 'admin' || userRole === 'energy_manager';
  const [rapport, setRapport] = useState(shareData);
  const [loading, setLoading] = useState(!shareData);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedPDL, setSelectedPDL] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);

  const pdfUrl = shareToken
    ? `${API_URL}/api/public/share/${shareToken}/pdf`
    : (id ? `${API_URL}/api/turpe/rapports/${id}/pdf` : null);
  const pdfFilename = `rapport_turpe_multi_${rapport?.client?.nom || rapport?.nom_entreprise || 'client'}_${new Date().toISOString().split('T')[0]}.pdf`;
  const printRef = useRef();

  useEffect(() => {
    if (shareData) {
      setRapport(shareData);
      setLoading(false);
      return;
    }
    const dataParam = searchParams.get('data');
    const previewId = searchParams.get('preview');
    
    if (previewId) {
      loadFromPreview(previewId);
    } else if (dataParam) {
      try {
        const decodedData = JSON.parse(decodeURIComponent(dataParam));
        setRapport(decodedData);
        setLoading(false);
      } catch (e) {
        console.error('Erreur décodage données:', e);
        loadRapport();
      }
    } else if (id) {
      loadRapport();
    } else {
      setLoading(false);
    }
  }, [id, searchParams, shareData]);

  const loadFromPreview = async (previewId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/turpe/rapports/multi/preview/${previewId}`);
      if (response.ok) {
        const data = await response.json();
        setRapport(data);
      }
    } catch (error) {
      console.error('Erreur chargement preview:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRapport = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/turpe/rapports/${id}`);
      if (response.ok) {
        const data = await response.json();
        setRapport(data);
      }
    } catch (error) {
      console.error('Erreur chargement rapport:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    const filename = `rapport_turpe_multi_${rapport?.client?.nom || 'client'}_${new Date().toISOString().split('T')[0]}.pdf`;
    try {
      if (shareToken) {
        await downloadPdf({ url: `/api/public/share/${shareToken}/pdf`, filename, includeAuth: false });
      } else {
        await downloadPdf({ url: `/api/turpe/rapports/multi/pdf`, method: 'POST', body: rapport, filename });
      }
    } catch (e) {
      // toast géré par l'utilitaire downloadPdf — on log pour debug uniquement
      console.warn('[RapportTURPEMulti] downloadPDF failed:', e?.message || e);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer ce rapport TURPE multi-sites ?')) return;
    const token = localStorage.getItem('token');
    await fetch(`${API_URL}/api/turpe/rapports/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
    navigate('/dashboard?tab=turpe');
  };

  const handleLock = async () => {
    if (!rapport) return;
    const token = localStorage.getItem('token');
    const action = rapport.locked ? 'unlock' : 'lock';
    await fetch(`${API_URL}/api/turpe/rapports/${id}/${action}`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
    loadRapport();
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null || isNaN(value)) return '0,00 €';
    return value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  };

  const formatNumber = (value, unit = '') => {
    if (value === undefined || value === null || isNaN(value)) return 'N/A';
    return value.toLocaleString('fr-FR', { maximumFractionDigits: 0 }) + (unit ? ` ${unit}` : '');
  };

  const formatPercent = (value) => {
    if (value === undefined || value === null || isNaN(value)) return '0%';
    return value.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + '%';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0EA5E9]"></div>
      </div>
    );
  }

  if (!rapport) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-gray-800 mb-2">Rapport non trouvé</h1>
          <p className="text-gray-500">Ce rapport n'existe pas ou les données ne sont pas disponibles</p>
        </div>
      </div>
    );
  }

  const { client, pdls = [], totaux = {} } = rapport;
  
  const totalActuel = totaux.totalActuel || pdls.reduce((sum, p) => sum + (p.calculs?.totalActuel || 0), 0);
  const totalPropose = totaux.totalPropose || pdls.reduce((sum, p) => sum + (p.calculs?.totalPropose || 0), 0);
  const economieTotal = totalActuel - totalPropose;
  const pourcentageEconomie = totalActuel > 0 ? (economieTotal / totalActuel) * 100 : 0;

  const getPeriodesForPDL = (pdl) => {
    const tension = pdl.tensionActuelle || 'HTA';
    return tension === 'HTA' ? ['Pointe', 'HPH', 'HCH', 'HPB', 'HCB'] : ['HPH', 'HCH', 'HPB', 'HCB'];
  };

  const getAllPeriodes = () => {
    const hasHTA = pdls.some(p => p.tensionActuelle === 'HTA');
    return hasHTA ? ['Pointe', 'HPH', 'HCH', 'HPB', 'HCB'] : ['HPH', 'HCH', 'HPB', 'HCB'];
  };

  const composantesNoms = {
    'CG': 'Composante de gestion',
    'CC': 'Composante de comptage',
    'CS Fixe': 'Composante de soutirage (fixe)',
    'CS Variable': 'Composante de soutirage (variable)',
    'CTA': 'Contribution Tarifaire d\'Acheminement',
    'CER': 'Composante de l\'énergie réactive',
    'CMDPS': 'Composante de dépassements'
  };

  // ==================== COMPOSANT: Onglet Vue d'ensemble ====================
  const OverviewTab = () => {
    const allPeriodes = getAllPeriodes();
    
    return (
      <div className="space-y-6">
        {(() => {
          const segChanges = pdls
            .map((pdl) => {
              const segA = tensionFromFormule(pdl.formuleActuelle) || pdl.tensionActuelle;
              const segP = tensionFromFormule(pdl.formuleProposee);
              if (segA && segP && segA !== segP) {
                return { numero: pdl.pdl?.numero || '-', segA, segP, formuleActuelle: pdl.formuleActuelle, formuleProposee: pdl.formuleProposee, tensionActuelle: pdl.tensionActuelle };
              }
              return null;
            })
            .filter(Boolean);
          if (segChanges.length === 0) return null;
          return (
            <div className="space-y-2" data-testid="segment-change-alert-multi">
              <p className="text-sm font-semibold text-amber-900 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                {segChanges.length} site{segChanges.length > 1 ? 's' : ''} avec changement de segment tarifaire
              </p>
              {segChanges.map((c) => (
                <div key={c.numero}>
                  <p className="text-xs font-mono text-gray-500 mb-1">PDL {c.numero}</p>
                  <SegmentChangeAlert
                    formuleActuelle={c.formuleActuelle}
                    formuleProposee={c.formuleProposee}
                    tensionActuelle={c.tensionActuelle}
                    compact
                  />
                </div>
              ))}
            </div>
          );
        })()}

        {/* Synthèse Financière */}
        <div className="bg-gradient-to-r from-[#0A2540] to-[#0D3A5F] rounded-xl p-8 text-white">
          <div className="flex items-center gap-3 mb-6">
            <Building2 className="w-6 h-6 text-cyan-400" />
            <h2 className="text-xl font-semibold">Synthèse Financière Globale</h2>
          </div>
          
          <div className="grid grid-cols-2 gap-8">
            {/* Avant */}
            <div className="bg-white/10 rounded-lg p-6">
              <p className="text-white/70 text-sm mb-2 uppercase tracking-wide">Situation Actuelle</p>
              <p className="text-4xl font-bold">{formatCurrency(totalActuel)}</p>
              <p className="text-white/60 text-sm mt-2">{pdls.length} sites • Coût annuel total</p>
            </div>
            
            {/* Après */}
            <div className="bg-cyan-500/20 rounded-lg p-6 border border-cyan-400/30">
              <p className="text-cyan-300 text-sm mb-2 uppercase tracking-wide">Situation Optimisée</p>
              <p className="text-4xl font-bold text-cyan-300">{formatCurrency(totalPropose)}</p>
              <div className="flex items-center gap-2 mt-2">
                <TrendingDown className="w-5 h-5 text-emerald-400" />
                <span className="text-emerald-400 font-medium">
                  {formatCurrency(economieTotal)} d'économies ({formatPercent(pourcentageEconomie)})
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bandeau méthode de calcul — courbe de charge vs facturation index */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 flex items-start gap-3" data-testid="turpe-methode-banner">
          <Info className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
          <div className="text-sm text-blue-900 leading-relaxed">
            <strong>Méthode de calcul : analyse de la courbe de charge.</strong> Les montants présentés
            (en particulier les dépassements de puissance) sont calculés à partir des <strong>courbes de
            charge réelles</strong> mesurées par Enedis (pas de 5 à 10 minutes). Votre fournisseur, lui,
            facture sur la base des <strong>index relevés</strong> : un léger écart entre ce rapport et vos
            factures est donc normal et n'affecte pas la pertinence de l'optimisation proposée.
          </div>
        </div>

        {/* Tableau Récapitulatif PDL avec Puissances */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-1">Récapitulatif des Sites</h3>
          <p className="text-gray-500 text-sm mb-6">Puissances souscrites avant/après par plage tarifaire</p>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-200 bg-gray-50">
                  <th className="text-left py-3 px-3 font-semibold text-gray-700" rowSpan={2}>PDL</th>
                  <th className="text-center py-3 px-3 font-semibold text-gray-700" rowSpan={2}>Tension</th>
                  <th className="text-center py-3 px-3 font-semibold text-gray-700" rowSpan={2}>Formule</th>
                  {allPeriodes.map(p => (
                    <th key={p} className="text-center py-2 px-2 font-semibold text-gray-600 border-l border-gray-200" colSpan={2}>
                      {p}
                    </th>
                  ))}
                  <th className="text-right py-3 px-3 font-semibold text-gray-700 border-l border-gray-200" rowSpan={2}>Économie</th>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  {allPeriodes.map(p => (
                    <React.Fragment key={`header-${p}`}>
                      <th className="text-center py-2 px-1 text-xs text-gray-500 border-l border-gray-100">Avant</th>
                      <th className="text-center py-2 px-1 text-xs text-cyan-600">Après</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pdls.map((pdl, idx) => {
                  const periodes = getPeriodesForPDL(pdl);
                  const pdlActuel = pdl.calculs?.totalActuel || 0;
                  const pdlPropose = pdl.calculs?.totalPropose || 0;
                  const pdlEconomie = pdlActuel - pdlPropose;
                  const unite = pdl.tensionActuelle === 'HTA' ? 'kW' : 'kVA';
                  
                  return (
                    <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-3 font-mono text-xs">{pdl.pdl?.numero || '-'}</td>
                      <td className="py-3 px-3 text-center">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          pdl.tensionActuelle === 'HTA' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {pdl.tensionActuelle || '-'}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-center text-xs">
                        <span className="text-gray-600">{pdl.formuleActuelle || '-'}</span>
                        {pdl.formuleProposee && pdl.formuleProposee !== pdl.formuleActuelle && (
                          <>
                            <span className="text-gray-400 mx-1">→</span>
                            <span className="text-cyan-600 font-medium">{pdl.formuleProposee}</span>
                          </>
                        )}
                      </td>
                      {allPeriodes.map(p => {
                        const hasThisPeriod = periodes.includes(p);
                        const pActuel = hasThisPeriod ? (pdl.calculs?.puissancesActuelles?.[p] || pdl.pdl?.puissanceSouscrite || 0) : null;
                        const pPropose = hasThisPeriod ? (pdl.calculs?.puissancesProposees?.[p] || pActuel) : null;
                        const isDifferent = pActuel !== pPropose;
                        
                        return (
                          <React.Fragment key={`${idx}-${p}`}>
                            <td className="py-3 px-1 text-center border-l border-gray-100 font-mono text-xs">
                              {hasThisPeriod ? formatNumber(pActuel) : '-'}
                            </td>
                            <td className={`py-3 px-1 text-center font-mono text-xs ${
                              hasThisPeriod && isDifferent ? 'text-cyan-600 font-semibold' : 'text-gray-400'
                            }`}>
                              {hasThisPeriod ? formatNumber(pPropose) : '-'}
                            </td>
                          </React.Fragment>
                        );
                      })}
                      <td className={`py-3 px-3 text-right font-medium border-l border-gray-200 ${
                        pdlEconomie > 0 ? 'text-emerald-600' : pdlEconomie < 0 ? 'text-red-600' : 'text-gray-400'
                      }`}>
                        {pdlEconomie > 0 ? '+' : ''}{formatCurrency(pdlEconomie)}
                      </td>
                    </tr>
                  );
                })}
                
                {/* Ligne Total */}
                <tr className="bg-gray-100 font-bold">
                  <td className="py-4 px-3" colSpan={3}>TOTAL PÉRIMÈTRE ({pdls.length} sites)</td>
                  <td colSpan={allPeriodes.length * 2} className="border-l border-gray-200"></td>
                  <td className={`py-4 px-3 text-right border-l border-gray-200 ${
                    economieTotal > 0 ? 'text-emerald-600' : economieTotal < 0 ? 'text-red-600' : 'text-gray-400'
                  }`}>
                    {economieTotal > 0 ? '+' : ''}{formatCurrency(economieTotal)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Graphique comparatif */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-1">Comparaison des Coûts par Site</h3>
          <p className="text-gray-500 text-sm mb-6">Visualisation des économies potentielles</p>
          
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={pdls.map((pdl, idx) => ({
                  name: pdl.pdl?.numero?.slice(-6) || `PDL ${idx + 1}`,
                  actuel: pdl.calculs?.totalActuel || 0,
                  propose: pdl.calculs?.totalPropose || 0
                }))}
                layout="vertical"
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis type="number" tickFormatter={(val) => `${(val/1000).toFixed(0)}k€`} />
                <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 11 }} />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                />
                <Legend />
                <Bar dataKey="actuel" name="Coût Actuel" fill="#94A3B8" radius={[0, 4, 4, 0]} />
                <Bar dataKey="propose" name="Coût Optimisé" fill="#0EA5E9" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Commentaires de l'analyste — Vue d'ensemble */}
        {(() => {
          const _pdlLabel = (p) => {
            const v = p.pdl ?? p.numero ?? '';
            if (typeof v === 'string' || typeof v === 'number') return String(v);
            if (v && typeof v === 'object') return v.numero || v.pdl || '';
            return '';
          };
          const commentairesPDL = (rapport?.pdls || pdls || [])
            .map((p) => ({ pdl: _pdlLabel(p), comment: (p.commentaire || '').trim() }))
            .filter((p) => p.comment);
          if (commentairesPDL.length === 0) return null;
          return (
            <div className="bg-white rounded-xl border border-gray-200 p-8" data-testid="section-commentaires">
              <h2 className="text-lg font-semibold text-[#191D1E] mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-cyan-500" /> Commentaires de l'analyste
              </h2>
              <div className="space-y-3">
                {commentairesPDL.map((p, idx) => (
                  <div key={idx} className="bg-cyan-50 border border-cyan-200 rounded-lg p-3">
                    <p className="text-xs font-semibold text-cyan-700 mb-1 font-mono">PDL {p.pdl}</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{p.comment}</p>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  // ==================== COMPOSANT: Onglet Analyse des Coûts ====================
  const CostsTab = () => {
    if (selectedPDL !== null) {
      const pdl = pdls[selectedPDL];
      const periodes = getPeriodesForPDL(pdl);
      const pdlActuel = pdl.calculs?.totalActuel || 0;
      const pdlPropose = pdl.calculs?.totalPropose || 0;
      const pdlEconomie = pdlActuel - pdlPropose;

      return (
        <div className="space-y-6">
          {/* Bouton retour */}
          <button 
            onClick={() => setSelectedPDL(null)}
            className="text-cyan-600 hover:text-cyan-700 flex items-center gap-2"
          >
            ← Retour au récapitulatif
          </button>

          {/* En-tête PDL */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    pdl.tensionActuelle === 'HTA' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {pdl.tensionActuelle || 'N/A'}
                  </span>
                  <span className="text-gray-500">{pdl.formuleActuelle}</span>
                  {pdl.formuleProposee && pdl.formuleProposee !== pdl.formuleActuelle && (
                    <>
                      <span className="text-gray-300">→</span>
                      <span className="text-cyan-600 font-medium">{pdl.formuleProposee}</span>
                    </>
                  )}
                </div>
                <h2 className="text-2xl font-bold text-gray-900">PDL {pdl.pdl?.numero || '-'}</h2>
              </div>
              <div className={`text-right px-6 py-4 rounded-lg ${
                pdlEconomie > 0 ? 'bg-emerald-50' : pdlEconomie < 0 ? 'bg-red-50' : 'bg-gray-50'
              }`}>
                <p className="text-sm text-gray-500 mb-1">Économie sur ce site</p>
                <p className={`text-2xl font-bold ${
                  pdlEconomie > 0 ? 'text-emerald-600' : pdlEconomie < 0 ? 'text-red-600' : 'text-gray-400'
                }`}>
                  {pdlEconomie > 0 ? '+' : ''}{formatCurrency(pdlEconomie)}
                </p>
              </div>
            </div>
          </div>

          {/* Courbe de charge */}
          {pdl.graphData && pdl.graphData.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-1">Courbe de charge</h3>
              <p className="text-gray-500 text-sm mb-4">Évolution de la puissance sur {pdl.graphData.length} points</p>
              
              {/* Légende des puissances de référence */}
              <div className="flex flex-wrap gap-4 mb-4 text-sm">
                {pdl.pdl?.puissanceSouscrite > 0 && (
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-0 border-t-2 border-dashed border-red-500"></div>
                    <span className="text-gray-600">P. Souscrite : <span className="font-semibold text-red-500">{pdl.pdl.puissanceSouscrite} {pdl.tensionActuelle === 'HTA' ? 'kW' : 'kVA'}</span></span>
                  </div>
                )}
                {pdl.calculs?.puissancePreconisee > 0 && pdl.calculs.puissancePreconisee !== pdl.pdl?.puissanceSouscrite && (
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-0 border-t-2 border-dashed border-cyan-500"></div>
                    <span className="text-gray-600">P. Préconisée : <span className="font-semibold text-cyan-500">{pdl.calculs.puissancePreconisee} {pdl.tensionActuelle === 'HTA' ? 'kW' : 'kVA'}</span></span>
                  </div>
                )}
              </div>
              
              <div className="h-72 bg-gray-50 rounded-lg p-4">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={pdl.graphData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 10, fill: '#6B7280' }}
                      tickFormatter={(val) => {
                        if (!val) return '';
                        const d = new Date(val);
                        return `${d.getDate()}/${d.getMonth()+1}/${String(d.getFullYear()).slice(-2)}`;
                      }}
                    />
                    <YAxis 
                      tick={{ fontSize: 10, fill: '#6B7280' }}
                      domain={[0, (dataMax) => {
                        const pSouscrite = pdl.pdl?.puissanceSouscrite || 0;
                        const pPreconisee = pdl.calculs?.puissancePreconisee || 0;
                        const maxRef = Math.max(pSouscrite, pPreconisee);
                        const maxVal = Math.max(dataMax || 0, maxRef);
                        return Math.ceil(maxVal * 1.1);
                      }]}
                      label={{ value: pdl.tensionActuelle === 'HTA' ? 'kW' : 'kVA', angle: -90, position: 'insideLeft', fontSize: 11 }}
                    />
                    <Tooltip 
                      labelFormatter={(val) => val ? new Date(val).toLocaleString('fr-FR') : ''}
                      formatter={(value, name) => [`${Number(value).toFixed(1)} ${pdl.tensionActuelle === 'HTA' ? 'kW' : 'kVA'}`, name]}
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                    />
                    <Legend />
                    <Area type="monotone" dataKey="kW" fill="#3B82F6" fillOpacity={0.1} stroke="none" />
                    <Line type="monotone" dataKey="kW" name="Puissance" stroke="#3B82F6" strokeWidth={1.5} dot={false} />
                    {pdl.pdl?.puissanceSouscrite > 0 && (
                      <ReferenceLine 
                        y={pdl.pdl.puissanceSouscrite} 
                        stroke="#EF4444" 
                        strokeDasharray="5 5" 
                        strokeWidth={2}
                      />
                    )}
                    {pdl.calculs?.puissancePreconisee > 0 && pdl.calculs.puissancePreconisee !== pdl.pdl?.puissanceSouscrite && (
                      <ReferenceLine 
                        y={pdl.calculs.puissancePreconisee} 
                        stroke="#0EA5E9" 
                        strokeDasharray="5 5" 
                        strokeWidth={2}
                      />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Tableau détaillé des coûts */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-1">Détail des Composantes</h3>
            <p className="text-gray-500 text-sm mb-4">Comparaison avant/après optimisation</p>
            
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 font-medium text-gray-600">Composante</th>
                  <th className="text-right py-3 font-medium text-gray-600">Actuel</th>
                  <th className="text-right py-3 font-medium text-cyan-600">Optimisé</th>
                  <th className="text-right py-3 font-medium text-gray-600">Écart</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(composantesNoms).map(([key, nom]) => {
                  const actuel = pdl.calculs?.composantes?.[key]?.actuel || 0;
                  const propose = pdl.calculs?.composantes?.[key]?.propose || 0;
                  const eco = actuel - propose;
                  
                  return (
                    <tr key={key} className="border-b border-gray-100">
                      <td className="py-3 text-gray-700">{nom}</td>
                      <td className="py-3 text-right text-gray-900">{formatCurrency(actuel)}</td>
                      <td className="py-3 text-right text-cyan-600">{formatCurrency(propose)}</td>
                      <td className={`py-3 text-right font-medium ${
                        eco > 0 ? 'text-emerald-500' : eco < 0 ? 'text-red-500' : 'text-gray-400'
                      }`}>
                        {eco !== 0 ? (eco > 0 ? '+' : '') + formatCurrency(eco) : '-'}
                      </td>
                    </tr>
                  );
                })}
                <tr className="bg-gray-50 font-bold">
                  <td className="py-4 text-gray-900">TOTAL</td>
                  <td className="py-4 text-right text-gray-900">{formatCurrency(pdlActuel)}</td>
                  <td className="py-4 text-right text-cyan-600">{formatCurrency(pdlPropose)}</td>
                  <td className={`py-4 text-right ${
                    pdlEconomie > 0 ? 'text-emerald-500' : pdlEconomie < 0 ? 'text-red-500' : 'text-gray-400'
                  }`}>
                    {pdlEconomie !== 0 ? (pdlEconomie > 0 ? '+' : '') + formatCurrency(pdlEconomie) : '-'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    // Vue récapitulatif des coûts
    return (
      <div className="space-y-6">
        {/* Tableau récapitulatif */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-1">Récapitulatif des Coûts</h3>
          <p className="text-gray-500 text-sm mb-6">Cliquez sur un site pour voir le détail et la courbe de charge</p>
          
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-200 bg-gray-50">
                <th className="text-left py-3 px-4 font-medium text-gray-600">PDL</th>
                <th className="text-center py-3 px-4 font-medium text-gray-600">Tension</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">Coût Actuel</th>
                <th className="text-right py-3 px-4 font-medium text-cyan-600">Coût Optimisé</th>
                <th className="text-right py-3 px-4 font-medium text-emerald-600">Économie</th>
                <th className="text-center py-3 px-4 font-medium text-gray-600">%</th>
                <th className="py-3 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {pdls.map((pdl, idx) => {
                const pdlActuel = pdl.calculs?.totalActuel || 0;
                const pdlPropose = pdl.calculs?.totalPropose || 0;
                const pdlEconomie = pdlActuel - pdlPropose;
                const pdlPourcent = pdlActuel > 0 ? (pdlEconomie / pdlActuel) * 100 : 0;
                
                return (
                  <tr 
                    key={idx} 
                    className="border-b border-gray-100 hover:bg-cyan-50 cursor-pointer transition-colors"
                    onClick={() => setSelectedPDL(idx)}
                  >
                    <td className="py-4 px-4 font-mono text-sm">{pdl.pdl?.numero || '-'}</td>
                    <td className="py-4 px-4 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        pdl.tensionActuelle === 'HTA' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {pdl.tensionActuelle || '-'}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-right text-gray-900">{formatCurrency(pdlActuel)}</td>
                    <td className="py-4 px-4 text-right text-cyan-600 font-medium">{formatCurrency(pdlPropose)}</td>
                    <td className={`py-4 px-4 text-right font-medium ${
                      pdlEconomie > 0 ? 'text-emerald-600' : pdlEconomie < 0 ? 'text-red-600' : 'text-gray-400'
                    }`}>
                      {pdlEconomie > 0 ? '+' : ''}{formatCurrency(pdlEconomie)}
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        pdlPourcent > 5 ? 'bg-emerald-100 text-emerald-700' : 
                        pdlPourcent > 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {formatPercent(pdlPourcent)}
                      </span>
                    </td>
                    <td className="py-4 px-2 text-cyan-500">
                      →
                    </td>
                  </tr>
                );
              })}
              
              {/* Ligne Total */}
              <tr className="bg-gray-100 font-bold">
                <td className="py-4 px-4" colSpan={2}>TOTAL ({pdls.length} sites)</td>
                <td className="py-4 px-4 text-right text-gray-900">{formatCurrency(totalActuel)}</td>
                <td className="py-4 px-4 text-right text-cyan-600">{formatCurrency(totalPropose)}</td>
                <td className={`py-4 px-4 text-right ${
                  economieTotal > 0 ? 'text-emerald-600' : economieTotal < 0 ? 'text-red-600' : 'text-gray-400'
                }`}>
                  {economieTotal > 0 ? '+' : ''}{formatCurrency(economieTotal)}
                </td>
                <td className="py-4 px-4 text-center">
                  <span className="px-2 py-1 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
                    {formatPercent(pourcentageEconomie)}
                  </span>
                </td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ==================== COMPOSANT: Onglet Détail Technique ====================
  const TechnicalTab = () => {
    if (selectedPDL !== null) {
      const pdl = pdls[selectedPDL];
      const periodes = getPeriodesForPDL(pdl);
      const unite = pdl.tensionActuelle === 'HTA' ? 'kW' : 'kVA';

      return (
        <div className="space-y-6">
          <button 
            onClick={() => setSelectedPDL(null)}
            className="text-cyan-600 hover:text-cyan-700 flex items-center gap-2"
          >
            ← Retour au récapitulatif
          </button>

          {/* En-tête */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                pdl.tensionActuelle === 'HTA' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
              }`}>
                {pdl.tensionActuelle || 'N/A'}
              </span>
              <span className="text-gray-500">{pdl.formuleActuelle}</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">PDL {pdl.pdl?.numero || '-'}</h2>
          </div>

          {/* Puissances */}
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Puissances Actuelles ({unite})</h3>
              <div className="space-y-3">
                {periodes.map((plage) => (
                  <div key={plage} className="flex justify-between py-2 border-b border-gray-100 last:border-0">
                    <span className="text-gray-600">{plage}</span>
                    <span className="font-mono font-medium text-gray-900">
                      {formatNumber(pdl.calculs?.puissancesActuelles?.[plage] || pdl.pdl?.puissanceSouscrite || 0)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-cyan-50 rounded-xl border-2 border-cyan-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Zap className="w-5 h-5 text-cyan-500" />
                <h3 className="font-semibold text-gray-900">Puissances Préconisées ({unite})</h3>
              </div>
              <div className="space-y-3">
                {periodes.map((plage) => {
                  const actuel = pdl.calculs?.puissancesActuelles?.[plage] || pdl.pdl?.puissanceSouscrite || 0;
                  const propose = pdl.calculs?.puissancesProposees?.[plage] || actuel;
                  const isDifferent = actuel !== propose;
                  const ecart = actuel - propose; // positif = baisse, négatif = hausse
                  
                  return (
                    <div key={plage} className="flex justify-between py-2 border-b border-cyan-200 last:border-0">
                      <span className="text-gray-600">{plage}</span>
                      <span className={`font-mono font-medium ${isDifferent ? 'text-cyan-600' : 'text-gray-500'}`}>
                        {formatNumber(propose)}
                        {isDifferent && (
                          <span className={`text-xs ml-2 ${ecart > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                            {ecart > 0 ? '↓' : '↑'}{formatNumber(Math.abs(ecart))}
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Consommations */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Consommations Horosaisonnières (kWh)</h3>
            <div className={`grid gap-4 ${periodes.length === 5 ? 'grid-cols-5' : 'grid-cols-4'}`}>
              {periodes.map((plage) => (
                <div key={plage} className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-gray-500 text-xs uppercase tracking-wide mb-2">{plage}</p>
                  <p className="text-xl font-bold text-gray-900">
                    {formatNumber(pdl.calculs?.consommations?.[plage] || 0)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    // Vue récapitulatif technique
    const allPeriodes = getAllPeriodes();
    
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-1">Données Techniques par Site</h3>
          <p className="text-gray-500 text-sm mb-6">Cliquez sur un site pour voir le détail</p>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-200 bg-gray-50">
                  <th className="text-left py-3 px-3 font-medium text-gray-600">PDL</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">Tension</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">Formule</th>
                  <th className="text-right py-3 px-3 font-medium text-gray-600">P. Max Souscrite</th>
                  <th className="text-right py-3 px-3 font-medium text-cyan-600">P. Préconisée</th>
                  <th className="text-right py-3 px-3 font-medium text-gray-600">Conso. Totale</th>
                  <th className="py-3 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {pdls.map((pdl, idx) => {
                  const periodes = getPeriodesForPDL(pdl);
                  const unite = pdl.tensionActuelle === 'HTA' ? 'kW' : 'kVA';
                  const pMax = Math.max(...periodes.map(p => pdl.calculs?.puissancesActuelles?.[p] || pdl.pdl?.puissanceSouscrite || 0));
                  const pPreco = pdl.calculs?.puissancePreconisee || pMax;
                  const consoTotale = periodes.reduce((sum, p) => sum + (pdl.calculs?.consommations?.[p] || 0), 0);
                  
                  return (
                    <tr 
                      key={idx} 
                      className="border-b border-gray-100 hover:bg-cyan-50 cursor-pointer transition-colors"
                      onClick={() => setSelectedPDL(idx)}
                    >
                      <td className="py-4 px-3 font-mono text-xs">{pdl.pdl?.numero || '-'}</td>
                      <td className="py-4 px-3 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          pdl.tensionActuelle === 'HTA' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {pdl.tensionActuelle}
                        </span>
                      </td>
                      <td className="py-4 px-3 text-center text-gray-600">{pdl.formuleActuelle || '-'}</td>
                      <td className="py-4 px-3 text-right font-mono">{formatNumber(pMax)} {unite}</td>
                      <td className={`py-4 px-3 text-right font-mono ${pPreco < pMax ? 'text-cyan-600 font-semibold' : 'text-gray-500'}`}>
                        {formatNumber(pPreco)} {unite}
                      </td>
                      <td className="py-4 px-3 text-right">{formatNumber(consoTotale)} kWh</td>
                      <td className="py-4 px-2 text-cyan-500">→</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" ref={printRef} data-testid="rapport-turpe-multi">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 print:static">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {!readOnly && (
              <button onClick={() => navigate('/dashboard?tab=turpe')} className="p-2 hover:bg-[#F5F6F9] rounded-lg print:hidden" data-testid="rapport-back-btn">
                <ArrowLeft className="w-5 h-5 text-[#687175]" />
              </button>
            )}
            <img src="/logo.jpg" alt="Place des Énergies" className="h-10" onError={(e) => { e.target.style.display = 'none'; }} />
            <span className="text-gray-400 text-sm">Rapport d'Optimisation TURPE — Multi-sites{readOnly ? ' — Lecture seule' : ''}</span>
          </div>
          <div className="flex gap-2 print:hidden">
            <MessageFournisseurButton
              pdls={pdls}
              client={client?.nom || rapport.nom_entreprise}
              siren={rapport.siren || client?.siren}
            />
            {readOnly ? (
              <>
                <button onClick={() => window.print()} className="p-2 hover:bg-[#F5F6F9] rounded-lg" title="Imprimer" data-testid="print-btn">
                  <Printer className="w-5 h-5 text-[#687175]" />
                </button>
                <button
                  onClick={() => setPdfPreviewOpen(true)}
                  className="flex items-center gap-2 bg-[#0891B2] text-white px-4 py-2 rounded-lg text-sm hover:bg-[#0E7490]"
                  data-testid="download-pdf-btn"
                >
                  <Download className="w-4 h-4" /> PDF
                </button>
              </>
            ) : id ? (
              <>
                <ShareAccessBadge rapportType="turpe_multi" rapportId={id} />
                <button onClick={() => setShareOpen(true)} className="p-2 hover:bg-[#F5F6F9] rounded-lg" title="Partager (lecture seule)" data-testid="share-btn">
                  <Share2 className="w-5 h-5 text-[#687175]" />
                </button>
                <ValideClientBadge
                  valide={rapport.valide_client}
                  canToggle={isAdmin}
                  endpoint={`/turpe/rapports/${rapport.id}/toggle-valide-client`}
                  onUpdated={(v) => setRapport({ ...rapport, valide_client: v })}
                />
                {isAdmin && (
                  <button onClick={handleLock} className="p-2 hover:bg-[#F5F6F9] rounded-lg" title={rapport.locked ? 'Déverrouiller' : 'Verrouiller'} data-testid="lock-btn">
                    {rapport.locked ? <Lock className="w-5 h-5 text-[#0891B2]" /> : <Unlock className="w-5 h-5 text-[#687175]" />}
                  </button>
                )}
                <button
                  onClick={() => setPdfPreviewOpen(true)}
                  className="flex items-center gap-2 bg-[#0891B2] text-white px-4 py-2 rounded-lg text-sm hover:bg-[#0E7490]"
                  data-testid="download-pdf-btn"
                >
                  <Download className="w-4 h-4" /> PDF
                </button>
                {isAdmin && (
                  <button onClick={handleDelete} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" data-testid="delete-btn"><Trash2 className="w-5 h-5" /></button>
                )}
              </>
            ) : (
              <button onClick={downloadPDF} className="flex items-center gap-2 bg-[#0891B2] text-white px-4 py-2 rounded-lg text-sm hover:bg-[#0E7490]">
                <Download className="w-4 h-4" /> PDF
              </button>
            )}
          </div>
        </div>
      </header>

      {!readOnly && id && (
        <ShareModal
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          rapportType="turpe_multi"
          rapportId={id}
          rapportLabel={rapport?.client?.nom || 'Rapport TURPE Multi-sites'}
        />
      )}

      <PdfPreviewModal
        open={pdfPreviewOpen}
        onClose={() => setPdfPreviewOpen(false)}
        url={pdfUrl}
        title={`Rapport TURPE Multi — ${rapport?.client?.nom || rapport?.nom_entreprise || ''}`}
        filename={pdfFilename}
      />

      {/* Titre */}
      <div className="max-w-7xl mx-auto px-6 pt-8 pb-2 print:hidden">
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight flex items-center gap-3">
          <Activity className="w-8 h-8 text-[#0891B2]" />
          {client?.nom?.toUpperCase() || 'CLIENT'}
        </h1>
        <div className="flex flex-wrap gap-4 mt-2 text-gray-500 text-sm">
          <span>Sites : <span className="font-semibold text-gray-700">{(rapport?.pdls || []).length} PDL</span></span>
          {client?.siren && (
            <>
              <span className="text-gray-300">|</span>
              <span>SIREN : <span className="font-mono">{client.siren}</span></span>
            </>
          )}
        </div>
      </div>

      {/* Onglets — format harmonisé avec TICFE/TICGN */}
      <div className="max-w-7xl mx-auto px-6 print:hidden">
        <div className="bg-[#EEF2F6] rounded-lg p-1 mb-6 flex">
          {[
            { id: 'overview', label: 'Vue d\'ensemble' },
            { id: 'costs', label: 'Analyse financière' },
            { id: 'technical', label: 'Détails PDL' },
            { id: 'informations', label: 'Informations' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setSelectedPDL(null); }}
              data-testid={`tab-${tab.id}`}
              className={`flex-1 py-2.5 px-4 text-sm font-medium rounded-md transition-all ${
                activeTab === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Contenu principal */}
      <main className="max-w-7xl mx-auto px-6 py-2">
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'costs' && <CostsTab />}
        {activeTab === 'technical' && <TechnicalTab />}
        {activeTab === 'informations' && (
          <div className="space-y-6" data-testid="tab-informations">
            <div className="bg-white rounded-xl border border-gray-200 p-8" data-testid="section-suite-a-donner">
              <h2 className="text-lg font-semibold text-[#191D1E] mb-4 flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-emerald-500" /> Suite à donner
              </h2>
              <div className="text-sm text-gray-600 leading-relaxed space-y-3">
                <p>En cas d'accord du client pour la mise en place de l'optimisation TURPE, les étapes suivantes seront engagées :</p>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>Validation des préconisations de puissance souscrite par PDL avec les équipes techniques du client</li>
                  <li>Préparation et envoi des avenants aux contrats d'acheminement / fourniture pour application des nouvelles formules tarifaires et puissances</li>
                  <li>Coordination avec ENEDIS pour l'application effective des changements (changement de puissance, mise à jour des plages horaires si concerné)</li>
                  <li>Suivi sur 12 mois de la facturation post-optimisation et ajustements le cas échéant</li>
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-8" data-testid="section-responsabilite">
              <h2 className="text-lg font-semibold text-[#191D1E] mb-4 flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-amber-500" /> Responsabilité
              </h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                Les estimations d'économies présentées sont établies sur la base des courbes de charge fournies par le client
                ou ENEDIS et des grilles tarifaires TURPE en vigueur. Place des Énergies ne saurait être tenue responsable en cas
                d'inexactitude ou d'incomplétude des données fournies, ni des évolutions réglementaires ultérieures (révisions
                tarifaires CRE, refonte des composantes TURPE). Les montants indiqués sont des estimations et peuvent varier en
                fonction des conditions réelles d'exploitation et de la facturation effective d'ENEDIS et du fournisseur.
              </p>
            </div>

            {!readOnly && id && (
              <RapportAuditHistory rapportType="turpe_multi" rapportId={id} />
            )}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 mt-12 print:mt-4">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between text-sm text-gray-400">
          <span>Place des Énergies — Rapport TURPE multi-sites généré le {new Date().toLocaleDateString('fr-FR')}</span>
          <span>{client?.nom || ''}</span>
        </div>
      </footer>

      <style>{`
        @media print {
          .print\\:hidden { display: none !important; }
          .print\\:static { position: static !important; }
          .print\\:mt-4 { margin-top: 1rem !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
};

export default RapportTURPEMulti;
